import { useEffect, useState, useCallback, useMemo } from 'react';
import { Server, AlertCircle, Monitor, RefreshCw, Settings, Activity, Shield } from 'lucide-react';
import { dashboardService } from '../services/dashboard.service';
import {
  dashboardSummaryService,
  type SecurityOverview,
  type DeviceFleetStatus,
  type DashboardSummary,
  type TrafficOverviewSummary,
  type ConfigComplianceSummary,
  type TopEdgeEntry,
} from '../services/dashboardSummary.service';
import type { Notification } from '../services/alerts.service';
import { useTenantStore } from '../stores/tenantStore';
import { useAuthStore } from '../stores/authStore';
import { USER_TYPES } from '../utils/constants';
import { useSettingsStore } from '../stores/settingsStore';
import { formatTimeOnly } from '../utils/formatDate';
import StatCard from '../components/common/StatCard';
import {
  SecurityPostureCard,
  FleetStatusCard,
  NotificationsWidget,
  ConfigComplianceCard,
  TrafficOverviewCard,
  TopEdgesCard,
} from '../components/dashboard';

interface VpnCounts {
  siteVpn: number;
  clientVpn: number;
}

const Dashboard = () => {
  const { getTenantId } = useTenantStore();
  // Subscribe to selectedTenantId as a value so this component re-renders
  // (and the fetchDashboardData useCallback rebuilds) on tenant switch.
  // Without this, getTenantId is a stable Zustand function reference, the
  // callback identity never changes, the useEffect never re-fires, and the
  // dashboard keeps showing the previous tenant's compliance / fleet /
  // traffic data — observed as cross-tenant MTGE sync state in the
  // Configuration Compliance card. (VSNP-670)
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { user } = useAuthStore();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [vpnCounts, setVpnCounts] = useState<VpnCounts>({ siteVpn: 0, clientVpn: 0 });
  const [deviceCount, setDeviceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Legacy dashboard data states
  const [securityOverview, setSecurityOverview] = useState<SecurityOverview | null>(null);
  const [fleetStatus, setFleetStatus] = useState<DeviceFleetStatus | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Enterprise dashboard data
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [trafficOverview, setTrafficOverview] = useState<TrafficOverviewSummary | null>(null);
  const [configCompliance, setConfigCompliance] = useState<ConfigComplianceSummary | null>(null);
  const [allComplianceDevices, setAllComplianceDevices] = useState<Array<{ id: string; name: string; status: string; deviceType?: 'edge' | 'connector' | 'mtge'; dirtyReason?: string; lastError?: string }>>([]);
  const [topEdges, setTopEdges] = useState<TopEdgeEntry[]>([]);
  const [useEnterpriseMode] = useState(true);

  const isSuperAdminAllTenants = user?.type === USER_TYPES.SUPER_ADMIN && !getTenantId();

  const fetchDashboardData = useCallback(async (isBackground = false) => {
    const tenantId = getTenantId();
    if (!isBackground) setLoading(true);

    const options = isBackground ? { showLoading: false } : undefined;

    try {
      // Try enterprise dashboard API first
      if (useEnterpriseMode) {
        // Fetch summary, traffic, and top ports data in parallel
        const [summary, traffic, notifs, edges, security] = await Promise.all([
          dashboardSummaryService.getDashboardSummary(tenantId, options),
          dashboardSummaryService.getTrafficOverviewViaPromQL(undefined, options),
          dashboardSummaryService.getRecentNotifications(),
          dashboardSummaryService.getTopEdgesViaPromQL(undefined, options),
          dashboardSummaryService.getSecurityOverview(options),
        ]);

        setTopEdges(edges);
        setSecurityOverview(security);

        if (summary) {
          setDashboardSummary(summary);
          setTrafficOverview(traffic);
          setNotifications(notifs);

          // Fetch config compliance from API (also uses config state, same source as /edges Config column)
          try {
            const complianceResult = await dashboardSummaryService.getConfigComplianceSummary(tenantId);
            if (complianceResult) {
              const allDevices = complianceResult.edges.map(e => ({
                id: e.id,
                name: e.name,
                status: e.status,
                deviceType: e.deviceType,
                dirtyReason: e.dirtyReason,
                lastError: e.lastError,
              }));

              setAllComplianceDevices(allDevices);
              setConfigCompliance({
                ...complianceResult.summary,
                edgesNeedingAttention: allDevices.filter(e => e.status !== 'synced' && e.status !== 'unknown').slice(0, 5),
              });
            }
          } catch (err) {
            console.error('Failed to fetch config compliance for dashboard:', err);
          }

          setLastRefreshed(new Date());
          if (!isBackground) setLoading(false);
          return;
        }

        // Enterprise summary failed — fetch config compliance independently
        const complianceResult = await dashboardSummaryService.getConfigComplianceSummary(tenantId, options);
        if (complianceResult) {
          setConfigCompliance(complianceResult.summary);
        }
        setTrafficOverview(traffic);
        setNotifications(notifs);
      }

      // Fallback to legacy API calls
      const [
        vpns,
        deviceConn,
        security,
        fleet,
        notifs,
      ] = await Promise.all([
        dashboardService.getVpnCounts(tenantId, options),
        dashboardService.getDeviceConnectionCount(tenantId, options),
        dashboardSummaryService.getSecurityOverview(options),
        dashboardSummaryService.getDeviceFleetStatus(options),
        dashboardSummaryService.getRecentNotifications(),
      ]);

      // Set VPN counts
      setVpnCounts({
        siteVpn: typeof vpns.siteVpn === 'number' ? vpns.siteVpn : 0,
        clientVpn: typeof vpns.clientVpn === 'number' ? vpns.clientVpn : 0,
      });

      // Set device count
      setDeviceCount(typeof deviceConn.count === 'number' ? deviceConn.count : 0);

      // Set new dashboard data
      setSecurityOverview(security);
      setFleetStatus(fleet);
      setNotifications(notifs);

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [getTenantId, useEnterpriseMode, selectedTenantId]);

  useEffect(() => {
    fetchDashboardData(false);
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboardData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Top Edges arrives from PromQL keyed only by edge_id (a GUID). Resolve a
  // friendly name, falling back to a short id when the edge isn't known yet.
  // Seed from fleetHealth (which resolves in the SAME fetch batch as topEdges,
  // so names are ready on first paint) then overlay the compliance device list.
  // Sourcing names only from allComplianceDevices — fetched after the parallel
  // batch — made the rows flash GUIDs until compliance loaded. (VSNP-966)
  const topEdgesResolved = useMemo(() => {
    const nameById = new Map<string, string>();
    dashboardSummary?.fleetHealth?.edgeHealthStatus?.forEach((e) => nameById.set(e.id, e.name));
    allComplianceDevices.forEach((d) => nameById.set(d.id, d.name));
    return topEdges.map((e) => ({
      ...e,
      edgeName: nameById.get(e.edgeId) || `Edge ${e.edgeId.slice(0, 8)}`,
    }));
  }, [topEdges, allComplianceDevices, dashboardSummary]);

  // SuperAdmin with "All Tenants" — show informational card instead of data
  if (isSuperAdminAllTenants) {
    return (
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Network operations center</p>
        </div>
        <div className="card p-8 text-center">
          <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Select a Tenant</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            Choose a tenant from the dropdown in the top menu bar to view
            dashboards, manage equipment, and configure network settings.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            While viewing all tenants, you can manage <strong>Users</strong>,{' '}
            <strong>Tenants</strong>, and <strong>System Settings</strong> from the
            Administration menu.
          </p>
        </div>
      </div>
    );
  }

  // Get alert count from dashboard summary or notifications
  const activeAlertCount = dashboardSummary?.kpis.activeAlerts ??
    notifications.filter((n) => n.type === 'error' || n.type === 'warning').length;

  // Prepare data for components
  // Note: totalBandwidthMbps comes from trafficOverview (PromQL), not the API.
  // vpnTunnels is an object {mesh, wgApps, ikev2Apps} — the old number shape is
  // a legacy stub left over from the V1 dev_site_vpn count (now dropped).
  const vpnTunnelsResp: any = dashboardSummary?.kpis.vpnTunnels;
  const vpnTunnels = vpnTunnelsResp && typeof vpnTunnelsResp === 'object'
    ? {
        mesh: Number(vpnTunnelsResp.mesh ?? 0),
        wgApps: Number(vpnTunnelsResp.wgApps ?? 0),
        ikev2Apps: Number(vpnTunnelsResp.ikev2Apps ?? 0),
      }
    : { mesh: 0, wgApps: 0, ikev2Apps: 0 };

  const kpis = {
    edgesOnline: dashboardSummary?.kpis.edgesOnline ?? fleetStatus?.onlineDevices ?? deviceCount,
    edgesTotal: dashboardSummary?.kpis.edgesTotal ?? fleetStatus?.totalDevices ?? deviceCount,
    configPending: dashboardSummary?.kpis.configPending ?? 0,
    configFailed: dashboardSummary?.kpis.configFailed ?? 0,
    totalBandwidthMbps: trafficOverview?.bandwidthMbps ?? 0,
    activeAlerts: dashboardSummary?.kpis.activeAlerts ?? activeAlertCount,
    vpnTunnels, // {mesh, wgApps, ikev2Apps}
    // Count of active edge peerings across every topology type. Falls back to
    // the mesh-only count for older API builds that don't send activePeerings.
    activePeerings: dashboardSummary?.kpis.activePeerings ?? vpnTunnels.mesh,
    clientVpnSessions: dashboardSummary?.kpis.clientVpnSessions ?? vpnCounts.clientVpn,
    aclEnabled: dashboardSummary?.kpis.aclEnabled ?? 0,
  };

  // Convert fleet health to DeviceFleetStatus format
  const fleetData: DeviceFleetStatus = dashboardSummary
    ? {
      totalDevices: dashboardSummary.fleetHealth.totalEdges,
      onlineDevices: dashboardSummary.fleetHealth.onlineEdges,
      offlineDevices: dashboardSummary.fleetHealth.offlineEdges,
      avgCpu: dashboardSummary.fleetHealth.avgCpu,
      avgMemory: dashboardSummary.fleetHealth.avgMemory,
      devices: dashboardSummary.fleetHealth.edgeHealthStatus.map(e => ({
        id: e.id,
        name: e.name,
        status: e.status === 'offline' ? 'offline' : 'online',
        cpu: e.cpu,
        memory: e.memory,
      })),
    }
    : fleetStatus ?? {
      totalDevices: 0,
      onlineDevices: 0,
      offlineDevices: 0,
      avgCpu: 0,
      avgMemory: 0,
      devices: [],
    };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Network operations center - real-time visibility</p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              Last updated: {formatTimeOnly(lastRefreshed, dateSettings)}
            </span>
            <button
              onClick={() => fetchDashboardData(false)}
              disabled={loading}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics - 6 KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Edges Online"
          value={kpis.edgesOnline}
          secondaryValue={`/${kpis.edgesTotal}`}
          icon={Monitor}
          color="primary"
          link="/monitoring/status"
          loading={loading}
          compact
          badge={
            kpis.edgesTotal > 0
              ? {
                status: kpis.edgesOnline === kpis.edgesTotal ? 'synced' : kpis.edgesOnline === 0 ? 'failed' : 'pending',
                label: `${Math.round((kpis.edgesOnline / kpis.edgesTotal) * 100)}%`,
              }
              : undefined
          }
        />
        <StatCard
          title="Config Status"
          value={kpis.configPending + kpis.configFailed}
          icon={Settings}
          color={kpis.configFailed > 0 ? 'danger' : kpis.configPending > 0 ? 'warning' : 'success'}
          link="/edges"
          loading={loading}
          compact
          badge={{
            status: kpis.configFailed > 0 ? 'failed' : kpis.configPending > 0 ? 'pending' : 'synced',
            label: kpis.configFailed > 0 ? `${kpis.configFailed} Failed` : kpis.configPending > 0 ? `${kpis.configPending} Pending` : 'All Synced',
          }}
        />
        <StatCard
          title="Traffic Rate"
          value={(() => {
            const mbps = kpis.totalBandwidthMbps;
            if (mbps >= 1000) return (mbps / 1000).toFixed(2);
            if (mbps >= 1) return mbps.toFixed(1);
            if (mbps >= 0.001) return (mbps * 1000).toFixed(0);
            return '0';
          })()}
          subtitle={kpis.totalBandwidthMbps >= 1000 ? 'Gbps' : kpis.totalBandwidthMbps >= 1 ? 'Mbps' : kpis.totalBandwidthMbps >= 0.001 ? 'Kbps' : 'bps'}
          icon={Activity}
          color="info"
          link="/analytics/traffic"
          loading={loading}
          compact
        />
        <StatCard
          title="Active Alerts"
          value={kpis.activeAlerts}
          icon={AlertCircle}
          color={kpis.activeAlerts > 0 ? 'warning' : 'success'}
          link="/notifications"
          loading={loading}
          compact
        />
        <StatCard
          title="Active Edge Peerings"
          value={kpis.activePeerings}
          icon={Server}
          color="success"
          link="/edge-peering"
          loading={loading}
          compact
        />
        <StatCard
          title="Security"
          value={kpis.aclEnabled}
          subtitle="ACL enabled"
          icon={Shield}
          color="secondary"
          link="/monitoring/acl"
          loading={loading}
          compact
        />
      </div>

      {/* Configuration Compliance & Network Traffic Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConfigComplianceCard
          data={configCompliance ?? dashboardSummary?.configCompliance ?? null}
          allDevices={allComplianceDevices}
          loading={loading}
        />
        <TrafficOverviewCard
          data={trafficOverview}
          loading={loading}
        />
      </div>

      {/* Fleet Health & Security Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FleetStatusCard data={fleetData} loading={loading} />
        <SecurityPostureCard
          data={securityOverview}
          loading={loading}
          aclStats={dashboardSummary?.security}
        />
      </div>

      {/* Top Applications & Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopEdgesCard
          data={topEdgesResolved}
          loading={loading}
        />
        <NotificationsWidget
          notifications={notifications}
          loading={loading}
          recentActivity={dashboardSummary?.recentActivity}
        />
      </div>

      {/*
        Removed the "Network Health" block (VSNP-663 #13).
        It duplicated stats already shown in the Edge Fleet, Security
        Posture, and Top Stats cards above, AND its "VPN Availability"
        gauge was hardcoded to deviceConnectivity (so 0 active peerings
        with 1 online edge would still show 100%). The reporter
        suggested removing it; we agreed.
      */}
    </div>
  );
};

export default Dashboard;
